import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useEmblaCarousel from "embla-carousel-react";
import { useEffect, useState } from "react";
import { FaPlay } from "react-icons/fa";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { toast } from "react-toastify";
import type { TChapter } from "../../services/seasons/types";
import {
  accessToLesson,
  accessToLessonVideo,
  getSeasonChapters,
  getSeasons,
  getUserLessons,
} from "../../services/seasons/utils";
import { Media } from "../media";
import { usePrevNextButtons } from "../seasons/hooks";

const LessonList = ({
  activeLessonId,
  onLessonChange,
  freeAccess = false,
}: {
  activeLessonId: number | null;
  onLessonChange: (id: number | null) => void;
  freeAccess?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    direction: "rtl",
    slidesToScroll: 1,
  });
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);
  const [currentLesson, setCurrentLesson] = useState<TChapter | null>(null);
  const { data: seasons } = useQuery({
    queryKey: ["seasons"],
    queryFn: () => getSeasons({ webinarId: 6 }),
  });

  const [activeSeasonId, setActiveSeasonId] = useState<number | null>(null);

  const { data: chapters } = useQuery({
    queryKey: ["seasons-chapters", activeSeasonId],
    queryFn: () =>
      activeSeasonId ? getSeasonChapters({ chapterId: activeSeasonId }) : [],
    enabled: !!activeSeasonId,
  });

  const { data: userLessons } = useQuery({
    queryKey: ["user-lessons"],
    queryFn: getUserLessons,
    // Poll until at least one lesson exists, then stop polling
    refetchInterval: (query) =>
      query.state.data && (query.state.data as TChapter[]).length > 0
        ? false
        : 1000,
    refetchOnWindowFocus: true,
  });

  const userLessonChapterIds = userLessons?.map((l) => l.id) || [];

  const sortedChapters = chapters
    ? [...chapters].sort((a, b) => {
        const aPriority = a.priority ?? a.id;
        const bPriority = b.priority ?? b.id;
        return aPriority - bPriority;
      })
    : [];

  useEffect(() => {
    if (activeLessonId) {
      localStorage.setItem("activeChapterId", String(activeLessonId));
    }
  }, [activeLessonId]);

  useEffect(() => {
    if (userLessons && userLessons.length > 0 && activeLessonId === null) {
      const lastLesson = userLessons[userLessons.length - 1];

      // Ensure the correct season is active and visible in the carousel
      const targetSeasonId = lastLesson.chapterId;
      setActiveSeasonId(targetSeasonId);

      // Scroll to the correct season in the carousel if possible
      if (seasons && emblaApi) {
        const seasonIndex = seasons.findIndex((s) => s.id === targetSeasonId);
        if (seasonIndex >= 0) {
          emblaApi.scrollTo(seasonIndex);
        }
      }

      // Select the last lesson
      onLessonChange(lastLesson.id);
      localStorage.setItem("activeChapterId", String(lastLesson.id));
    }
  }, [
    userLessons,
    activeLessonId,
    chapters,
    onLessonChange,
    seasons,
    emblaApi,
  ]);

  useEffect(() => {
    if (chapters && activeLessonId) {
      const foundLesson = chapters.find((c) => c.id === activeLessonId);
      if (foundLesson) {
        setCurrentLesson(foundLesson);
      }
      // Removed: else block that changes activeLessonId when not found in current season
    }
  }, [chapters, activeLessonId, userLessonChapterIds, onLessonChange]);

  // New effect: when activeLessonId changes, update activeSeasonId and scroll to the correct season
  useEffect(() => {
    if (activeLessonId && userLessons && seasons && emblaApi) {
      const lesson = userLessons.find((l) => l.id === activeLessonId);
      if (lesson) {
        setActiveSeasonId(lesson.chapterId);
        const seasonIndex = seasons.findIndex((s) => s.id === lesson.chapterId);
        if (seasonIndex >= 0) {
          emblaApi.scrollTo(seasonIndex);
        }
      }
    }
  }, [activeLessonId, userLessons, seasons, emblaApi]);

  const { data: lessonVideo } = useQuery({
    queryKey: ["lesson-video", activeLessonId],
    queryFn: () => accessToLessonVideo({ lessonId: activeLessonId! }),
    enabled: !!activeLessonId,
  });

  const { mutate: accessUserToLesson } = useMutation({
    mutationFn: accessToLesson,
    onSuccess: () => {
      toast.success("دسترسی به درس ثبت شد");
    },
    onError: () => {
      toast.error("دسترسی ثبت نشد");
    },
  });

  const handleComplete = async (
    currentLessonId: number | null,
    opts?: { auto?: boolean }
  ) => {
    console.log("🔍 handleComplete called:");
    console.log("  - currentLessonId (from param):", currentLessonId);
    console.log("  - activeLessonId (state):", activeLessonId);
    console.log("  - auto mode:", opts?.auto);
    
    if (!currentLessonId || !userLessons || !seasons) return;

    // Find which season/chapter the current lesson belongs to
    const currentUserLesson = userLessons.find((l) => l.id === activeLessonId);
    if (!currentUserLesson) {
      console.log("❌ Current lesson not found in userLessons");
      return;
    }

    const currentSeasonId = currentUserLesson.chapterId;
    console.log("  - currentSeasonId:", currentSeasonId);

    // Get all chapters for the current season
    let currentSeasonChapters: TChapter[] = [];
    try {
      currentSeasonChapters = await getSeasonChapters({ chapterId: currentSeasonId });
      currentSeasonChapters.sort((a, b) => {
        const aPriority = a.priority ?? a.id;
        const bPriority = b.priority ?? b.id;
        return aPriority - bPriority;
      });
      console.log("  - currentSeasonChapters:", currentSeasonChapters.length);
    } catch (error) {
      console.error("Failed to fetch current season chapters:", error);
      return;
    }

    // Find the next chapter in the current season
    const activeIndex = currentSeasonChapters.findIndex((c) => c.id === activeLessonId);
    console.log("  - activeIndex in currentSeasonChapters:", activeIndex);
    let nextChapter = currentSeasonChapters[activeIndex + 1];
    console.log("  - nextChapter in same season:", nextChapter ? `${nextChapter.id}: ${nextChapter.title}` : "undefined");
    let targetSeasonId = currentSeasonId;

    // If no next chapter in current season, find next season's first lesson
    if (!nextChapter) {
      // In auto mode, don't jump to next season - only allow manual transition
      if (opts?.auto) {
        console.log(
          "آخرین درس فصل تمام شد - فقط با دکمه درس بعدی می‌توانید به فصل بعدی بروید"
        );
        return;
      }

      const currentSeasonIndex = seasons.findIndex((s) => s.id === currentSeasonId);
      const nextSeason = seasons[currentSeasonIndex + 1];

      if (nextSeason) {
        console.log("  - Moving to next season:", nextSeason.id);
        targetSeasonId = nextSeason.id;

        // Get the first lesson of the next season
        try {
          const nextSeasonChapters = await getSeasonChapters({
            chapterId: nextSeason.id,
          });
          if (nextSeasonChapters && nextSeasonChapters.length > 0) {
            nextSeasonChapters.sort((a, b) => {
              const aPriority = a.priority ?? a.id;
              const bPriority = b.priority ?? b.id;
              return aPriority - bPriority;
            });
            nextChapter = nextSeasonChapters[0];
            console.log("  - nextChapter from next season:", nextChapter.id, nextChapter.title);
          }
        } catch (error) {
          console.error("Failed to fetch next season chapters:", error);
          return;
        }
      }
    }

    if (!nextChapter) {
      console.log("❌ آخرین درس تمام شد!");
      return;
    }

    const isNextUnlocked = userLessonChapterIds.includes(nextChapter.id);
    console.log("  - isNextUnlocked:", isNextUnlocked);
    console.log("  - isNextUnlocked:", isNextUnlocked);
    console.log("  - userLessonChapterIds:", userLessonChapterIds);

    // Auto mode: only enroll/switch if next lesson is NOT unlocked
    // DISABLED: Automatic lesson opening is commented out
    if (opts?.auto) {
      // Do nothing in auto mode - automatic progression is disabled
      return;
      // if (isNextUnlocked) {
      //   // Do nothing in auto mode when next is already unlocked
      //   return;
      // }
      // accessUserToLesson(
      //   { lessonId: nextChapter.id },
      //   {
      //     onSuccess: async () => {
      //       await queryClient.invalidateQueries({ queryKey: ["user-lessons"] });
      //       await queryClient.invalidateQueries({
      //         queryKey: ["lesson-video", nextChapter.id],
      //       });
      //       await queryClient.invalidateQueries({
      //         queryKey: ["seasons-chapters", targetSeasonId],
      //       });

      //       onLessonChange(nextChapter.id);
      //       setCurrentLesson(nextChapter);
      //       localStorage.setItem("activeChapterId", String(nextChapter.id));
      //     },
      //   }
      // );
      // return;
    }

    if (isNextUnlocked) {
      console.log("✅ Next lesson is already unlocked, switching to it");
      // If the next lesson is in a different season, switch to that season
      if (targetSeasonId !== activeSeasonId) {
        console.log("  - Switching to different season:", targetSeasonId);
        setActiveSeasonId(targetSeasonId);
        
        // Scroll the carousel to the correct season
        if (emblaApi) {
          const seasonIndex = seasons.findIndex((s) => s.id === targetSeasonId);
          if (seasonIndex >= 0) {
            emblaApi.scrollTo(seasonIndex);
          }
        }
        
        // Wait for the UI to update
        setTimeout(() => {
          onLessonChange(nextChapter.id);
          setCurrentLesson(nextChapter as TChapter);
          localStorage.setItem("activeChapterId", String(nextChapter.id));
          const el = document.getElementById(`chapter-${nextChapter.id}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
      } else {
        console.log("  - Same season, just switching lesson");
        onLessonChange(nextChapter.id);
        setCurrentLesson(nextChapter as TChapter);
        localStorage.setItem("activeChapterId", String(nextChapter.id));
        setTimeout(() => {
          const el = document.getElementById(`chapter-${nextChapter.id}`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 400);
      }
      return;
    }

    // Next lesson is locked - enroll then switch
    console.log("🔓 Next lesson is locked, enrolling user...");
    accessUserToLesson(
      { lessonId: nextChapter.id },
      {
        onSuccess: async () => {
          console.log("✅ Successfully enrolled in lesson:", nextChapter.id);
          await queryClient.invalidateQueries({ queryKey: ["user-lessons"] });
          await queryClient.invalidateQueries({
            queryKey: ["lesson-video", nextChapter.id],
          });
          await queryClient.invalidateQueries({
            queryKey: ["seasons-chapters", targetSeasonId],
          });

          // If switching to a different season, scroll to that season first
          if (targetSeasonId !== activeSeasonId) {
            setActiveSeasonId(targetSeasonId);
            
            // Scroll the carousel to the correct season
            if (emblaApi) {
              const seasonIndex = seasons.findIndex((s) => s.id === targetSeasonId);
              if (seasonIndex >= 0) {
                emblaApi.scrollTo(seasonIndex);
              }
            }
            
            setTimeout(() => {
              onLessonChange(nextChapter.id);
              setCurrentLesson(nextChapter as TChapter);
              localStorage.setItem("activeChapterId", String(nextChapter.id));
              const el = document.getElementById(`chapter-${nextChapter.id}`);
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }, 100);
          } else {
            onLessonChange(nextChapter.id);
            setCurrentLesson(nextChapter as TChapter);
            localStorage.setItem("activeChapterId", String(nextChapter.id));
            setTimeout(() => {
              const el = document.getElementById(`chapter-${nextChapter.id}`);
              if (el) {
                el.scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }, 400);
          }
        },
      }
    );
  };

  useEffect(() => {
    if (!emblaApi || !seasons) return;

    const onSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      const season = seasons[index];
      if (season) {
        setActiveSeasonId(season.id);
      }
    };

    emblaApi.on("select", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, seasons]);

  useEffect(() => {
    if (seasons && seasons.length > 0 && !activeSeasonId) {
      setActiveSeasonId(seasons[0].id);
    }
  }, [seasons, activeSeasonId]);

  useEffect(() => {
    const allKeys = Object.keys(localStorage);
    const lessonKeys = allKeys
      .filter((key) => key.startsWith("lesson_"))
      .map((key) => Number(key.replace("lesson_", "")))
      .filter((num) => !isNaN(num));

    if (lessonKeys.length === 0) return;

    const lastLessonId = Math.max(...lessonKeys);

    onLessonChange(lastLessonId);
    setCurrentLesson({ id: lastLessonId } as TChapter);

    setTimeout(() => {
      const el = document.getElementById(`chapter-${lastLessonId}`);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 400);
  }, [onLessonChange]);

  const currentVideo = lessonVideo?.find((video) => video.isMasterHls);

  return (
    <div>
      <div
        className="overflow-hidden max-w-4xl mx-auto mt-36 md:mt-96 relative"
        ref={emblaRef}
      >
        <div className="flex gap-6">
          {seasons?.map((season) => (
            <div
              key={season.id}
              className="flex-[0_0_calc(100%_-_1.5rem)] p-5 shrink-0"
            >
              <div className="text-center mb-6">
                <h5 className="font-kalameh text-4xl md:text-5xl">
                  {season.title}
                </h5>
                <p className="font-kalameh text-2xl md:text-4xl">
                  {season.description}
                </p>
              </div>

              <div className="space-y-4 px-4 md:px-12 py-8 h-[328px] md:h-[400px] w-10/12 mx-auto overflow-y-auto rounded-3xl bg-white/20">
                {sortedChapters.map((chapter) => {
                  const isUnlocked = freeAccess || userLessonChapterIds.includes(chapter.id);
                  const isCurrent =
                    chapter.id === currentLesson?.id ||
                    chapter.id ===
                      Number(localStorage.getItem("activeChapterId"));

                  return (
                    <div
                      key={chapter.id}
                      id={`chapter-${chapter.id}`}
                      className={`flex items-center justify-between px-4 cursor-pointer gap-4 py-2 rounded-2xl ${
                        isCurrent ? "bg-black text-white" : ""
                      }`}
                      onClick={() => {
                        if (isUnlocked) {
                          if (freeAccess && !userLessonChapterIds.includes(chapter.id)) {
                            accessUserToLesson(
                              { lessonId: chapter.id },
                              {
                                onSuccess: async () => {
                                  await queryClient.invalidateQueries({ queryKey: ["user-lessons"] });
                                  await queryClient.invalidateQueries({ queryKey: ["lesson-video", chapter.id] });
                                  onLessonChange(chapter.id);
                                  setCurrentLesson(chapter);
                                  localStorage.setItem("activeChapterId", String(chapter.id));
                                },
                              }
                            );
                          } else {
                            onLessonChange(chapter.id);
                            setCurrentLesson(chapter);
                            localStorage.setItem("activeChapterId", String(chapter.id));
                          }
                        }
                      }}
                    >
                      <p
                        className={`text-white text-lg md:text-2xl font-kalameh ${
                          isUnlocked ? "opacity-100" : "opacity-60"
                        }`}
                      >
                        {chapter.title}
                      </p>
                      {isCurrent ? (
                        <FaPlay className="text-2xl md:text-4xl" />
                      ) : (
                        <img
                          src={
                            isUnlocked
                              ? "/images/check.png"
                              : "/images/lock.png"
                          }
                          alt={isUnlocked ? "check" : "lock"}
                          className="w-6 h-6 md:w-12 md:h-12"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <button
          className="size-11 absolute top-[52%] right-0"
          onClick={onPrevButtonClick}
          disabled={prevBtnDisabled}
        >
          <FiArrowRight
            className={`text-4xl font w-full ${
              prevBtnDisabled ? "text-white/35" : "text-white"
            }`}
          />
        </button>
        <button
          className="size-11 absolute top-[52%] left-0"
          onClick={onNextButtonClick}
          disabled={nextBtnDisabled}
        >
          <FiArrowLeft
            className={`text-4xl font w-full ${
              nextBtnDisabled ? "text-white/35" : "text-white"
            }`}
          />
        </button>
      </div>

      <div className="md:mt-10">
        {currentVideo ? (
          <Media
            video={currentVideo}
            onComplete={handleComplete}
            chapter_name={currentLesson?.title}
            freeAccess={freeAccess}
          />
        ) : (
          <div className="w-[366px] md:w-7/12 lg:w-5/12 h-52 md:h-72 lg:h-96 mx-auto mt-10 rounded-4xl bg-gray-400 animate-pulse" />
        )}
      </div>
    </div>
  );
};

export default LessonList;
